import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import settings from 'settings';
import { convertToSelect, getKey, getLabel } from 'library/helpers';
import {
  formatTravelLegList, getBookingSummary, getLegKeyLabel, getLegLabel
} from 'selectors/travel.selectors';
import _ from 'lodash';
import {
  Col, DatePicker, Form, Input, Row, TimePicker
} from 'antd';
import ExtendedSelect from 'components/extendedSelect/extended-select';
import TagSelector from 'components/tagSelector/tag-selector';
import { FormWrapper } from 'components/Page';
import REDUCER_KEY from 'reducers/reducer.keys';
import { list } from 'actions';

const FormItem = Form.Item;

const mapStateToProps = (state, ownProps) => ({
  routes: state.api.travelRoutes.data.results,
  routesInFlight: state.api.travelRoutes.inFlight,
  legs: formatTravelLegList(state.api.travelLegs.data.results),
  legsInFlight: state.api.travelLegs.inFlight,
  statuses: state.api.travelStatuses.data.results,
  statusesInFlight: state.api.travelStatuses.inFlight,
  travelModes: typeof state.api.travelModes.data.results !== 'undefined' ? [] : state.api.travelModes.data, // api returns different types
  bookingSummary: getBookingSummary(state.api.travelLegs.data.results, ownProps.dataSource)
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  loadRoutes(searchText = '') {
    // get the dependent travel_mode
    const travelMode = ownProps.form.getFieldValue('travel_mode');
    // reset the dependent fields
    ownProps.form.setFieldsValue({ leg: { key: '' } });
    ownProps.form.resetFields(['leg']);
    if (travelMode) {
      // build filters
      const page = 0;
      const pageSize = settings.APP_CONFIG.dropListSize;
      const sorted = [{
        id: 'name',
        desc: false
      }];
      const filters = [
        {
          id: 'travel_mode__icontains',
          value: travelMode
        },
        {
          id: 'active',
          value: true
        }
      ];

      if (!_.isEmpty(searchText)) {
        filters.push({
          id: 'name__icontains',
          value: searchText
        });
      }
      return dispatch(list(REDUCER_KEY.travelRoutes.key, {
        page,
        pageSize,
        filters,
        sorted
      }));
    }

    return false;
  },
  loadLegs(searchText) {
    const route = ownProps.form.getFieldValue('route').key;
    // A route is needed to provide the right legs
    if (route) {
      // build filters
      const page = 0;
      const pageSize = settings.APP_CONFIG.dropListSize;
      const sorted = [{
        id: 'name',
        desc: false
      }];
      const filters = [
        {
          id: 'route__id',
          value: route
        },
        {
          id: 'active',
          value: true
        }
      ];

      if (!_.isEmpty(searchText)) {
        filters.push({
          id: 'reference_number__icontains',
          value: searchText
        });
      }
      return dispatch(list(REDUCER_KEY.travelLegs.key, {
        page,
        pageSize,
        filters,
        sorted
      }));
    }

    return false;
  },
  loadStatus(searchText) {
    // build filters
    const page = 0;
    const pageSize = settings.APP_CONFIG.dropListSize;
    const sorted = [{
      id: 'name',
      desc: false
    }];
    const filters = [];

    if (!_.isEmpty(searchText)) {
      filters.push({
        id: 'name__icontains',
        value: searchText
      });
    }
    filters.push({
      id: 'active',
      value: true
    });
    return dispatch(list(REDUCER_KEY.travelStatuses.key, {
      page,
      pageSize,
      filters,
      sorted
    }));
  },
  loadTravelModes(searchText) {
    // reset the dependent fields
    ownProps.form.setFieldsValue({
      route: undefined,
      leg: { key: '' }
    });
    ownProps.form.resetFields(['leg', 'route']);
    // build filters
    const page = 0;
    const pageSize = settings.APP_CONFIG.dropListSize;
    const sorted = [{
      id: 'name',
      desc: false
    }];
    const filters = [];

    if (!_.isEmpty(searchText)) {
      filters.push({
        id: 'name__icontains',
        value: searchText
      });
    }
    filters.push({
      id: 'active',
      value: true
    });
    return dispatch(list(REDUCER_KEY.travelModes.key, {
      page,
      pageSize,
      filters,
      sorted
    }));
  }
});

const mapPropsToFields = ({ dataSource }) => {
  const {
    arrival_date,
    arrival_time,
    departure_date,
    departure_time,
    leg,
    reference_number,
    route,
    status,
    travel_mode,
    itemTags
  } = dataSource;

  const departureTime = departure_time || departure_date;
  const arrivalTime = arrival_time || arrival_date;

  return {
    reference_number: Form.createFormField({ value: reference_number }),
    status: Form.createFormField({ value: convertToSelect(status) }),
    travel_mode: Form.createFormField({ value: travel_mode }),
    route: Form.createFormField({ value: convertToSelect(route) }),
    leg: Form.createFormField({ value: getLegKeyLabel(leg) }),
    departure_date: Form.createFormField({ value: departure_date }),
    departure_time: Form.createFormField({ value: departureTime }),
    arrival_date: Form.createFormField({ value: arrival_date }),
    arrival_time: Form.createFormField({ value: arrivalTime }),
    itemTags: Form.createFormField({ value: itemTags })
  };
};

const onFieldsChange = (props, fields) => {
  const { onFieldsChange: dispatchOnFieldsChange } = props;
  if (dispatchOnFieldsChange) {
    dispatchOnFieldsChange(fields);
  }
};

const onValuesChange = (props, values) => {
  const { onValuesChange: dispatchOnValuesChange } = props;
  if (dispatchOnValuesChange) {
    dispatchOnValuesChange(values);
  }
};

const FormInput = ({
  canAdmin,
  bookingSummary,
  dataSource,
  form,
  inputFormItemLayout,
  legs,
  legsInFlight,
  routes,
  routesInFlight,
  statuses,
  statusesInFlight,
  travelModes,
  travelModesInFlight,
  loadRoutes,
  loadLegs,
  loadStatus,
  loadTravelModes,
  themeName
}) => {
  const { getFieldDecorator } = form;
  const {
    arrival_date,
    arrival_time,
    departure_date,
    departure_time,
    leg,
    reference_number,
    route,
    status,
    travel_mode,
    itemTags
  } = dataSource;
  const readonly = !canAdmin;
  /*
   ** @description Update the times based on the selected leg
   ** @params input object
   */
  const handleLegChange = (input) => {
    const currentLeg = legs.find(item => item.id === input.key);
    form.setFieldsValue({ departure_time: currentLeg.departure_time });
    form.setFieldsValue({ arrival_time: currentLeg.arrival_time });
  };

  return (
    <Form>
      <Row {...inputFormItemLayout.row}>
        <Col {...inputFormItemLayout.col}>
          <FormItem {...inputFormItemLayout.inputLayout} label="Booking No">
            {getFieldDecorator('reference_number', {
              initialValue: reference_number
            })(<Input disabled={readonly} />)}
          </FormItem>
          <FormItem {...inputFormItemLayout.inputLayout} label="Travel Mode">
            {getFieldDecorator('travel_mode', {
              initialValue: travel_mode,
              rules: [
                {
                  required: true,
                  message: 'Please select the Travel Mode.'
                }
              ]
            })(
              <ExtendedSelect
                placeholder="Select Travel Mode"
                fetching={travelModesInFlight}
                dataSource={travelModes}
                optionKey={item => item.key}
                optionLabel={item => item.value}
                onSearch={loadTravelModes}
                onFocus={loadTravelModes}
                labelInValue={false}
              />
            )}
          </FormItem>
          <FormItem {...inputFormItemLayout.inputLayout} label="Route">
            {getFieldDecorator('route', {
              initialValue: route,
              rules: [{
                required: true,
                message: 'Please select a Route.'
              }]
            })(
              <ExtendedSelect
                placeholder="Select Route"
                fetching={routesInFlight}
                dataSource={routes}
                optionKey={getKey}
                optionLabel={getLabel}
                onSearch={loadRoutes}
                onFocus={loadRoutes}
                disabled={readonly || !travel_mode}
              />
            )}
          </FormItem>
          <FormItem {...inputFormItemLayout.inputLayout} label="Leg">
            {getFieldDecorator('leg', {
              initialValue: getLegKeyLabel(leg),
              rules: [{
                required: true,
                message: 'Please select a Leg.'
              }]
            })(
              <ExtendedSelect
                placeholder="Select Leg"
                dropdownClassName="bhp-booking-form-input bhp-leg-input"
                fetching={legsInFlight}
                dataSource={legs}
                optionKey={getKey}
                optionLabel={getLegLabel}
                onSearch={loadLegs}
                onFocus={loadLegs}
                onChange={handleLegChange}
                disabled={readonly || !(route ? route.key || route.id : false)} // test for valid route identifier
              />
            )}
          </FormItem>
          <FormItem {...inputFormItemLayout.inputLayout} label="Status">
            {getFieldDecorator('status', {
              initialValue: status,
              rules: [{
                required: true,
                message: 'Please select a Status.'
              }]
            })(
              <ExtendedSelect
                placeholder="Select Status"
                showSearch={false}
                fetching={statusesInFlight}
                dataSource={statuses}
                optionKey={getKey}
                optionLabel={getLabel}
                onSearch={loadStatus}
                onFocus={loadStatus}
                disabled={readonly}
              />
            )}
          </FormItem>
        </Col>
        <Col {...inputFormItemLayout.col}>
          <FormItem {...inputFormItemLayout.inputLayout} label="Departure Date">
            {getFieldDecorator('departure_date', {
              initialValue: departure_date,
              rules: [
                {
                  required: true,
                  message: 'Please select the Departure Date.'
                }
              ]
            })(<DatePicker format="DD-MM-YYYY" disabled={readonly} />)}
          </FormItem>
          <FormItem {...inputFormItemLayout.inputLayout} label="Departure Time">
            {getFieldDecorator('departure_time', {
              initialValue: departure_time,
              rules: [
                {
                  required: true,
                  message: 'Please select the Departure Time.'
                }
              ]
            })(<TimePicker format="HH:mm" disabled={readonly} />)}
          </FormItem>
          <FormItem {...inputFormItemLayout.inputLayout} label="Arrival Date">
            {getFieldDecorator('arrival_date', {
              initialValue: arrival_date,
              rules: [
                {
                  required: true,
                  message: 'Please select the Arrival Date.'
                }
              ]
            })(<DatePicker format="DD-MM-YYYY" disabled={readonly} />)}
          </FormItem>
          <FormItem {...inputFormItemLayout.inputLayout} label="Arrival Time">
            {getFieldDecorator('arrival_time', {
              initialValue: arrival_time,
              rules: [
                {
                  required: true,
                  message: 'Please select the Arrival Time.'
                }
              ]
            })(<TimePicker format="HH:mm" disabled={readonly} />)}
          </FormItem>
          <FormItem {...inputFormItemLayout.inputLayout} label="Tags">
            {getFieldDecorator('itemTags', {
              initialValue: itemTags
            })(
              <TagSelector
                theme={themeName}
                placeholder="Select Tags"
                tagSelectorReducerKey={REDUCER_KEY.bookingCollectionValues}
                disabled={readonly}
                maxTagCount={3}
              />
            )}
          </FormItem>
        </Col>
      </Row>
      {dataSource && dataSource.id > 0 && (
        <fieldset className="bhp-fieldset bhp-tab">
          <legend>
            Booking Summary
          </legend>
          {bookingSummary.map(item => (
            <FormItem key={item.title} {...inputFormItemLayout.summaryLayout} label={item.title} className="bhp-form-item-text">
              {item.description}
            </FormItem>
          ))}
        </fieldset>
      )}
    </Form>
  );
};

FormInput.propTypes = {
  bookingSummary: PropTypes.array,
  canAdmin: PropTypes.bool,
  checkboxFormItemLayout: PropTypes.object,
  dataSource: PropTypes.object,
  form: PropTypes.object,
  inputFormItemLayout: PropTypes.object,
  itemTags: PropTypes.array,
  loadTypes: PropTypes.func,
  loadStatus: PropTypes.func,
  statuses: PropTypes.array,
  statusesInFlight: PropTypes.bool,
  itemTagsInFlight: PropTypes.bool,
  onFieldsChange: PropTypes.func,
  onValuesChange: PropTypes.func,
  readonly: PropTypes.bool,
  themeName: PropTypes.string,
  loadTravelModes: PropTypes.func,
  travelModes: PropTypes.array,
  travelModesInFlight: PropTypes.bool,
  legs: PropTypes.array,
  legsInFlight: PropTypes.bool,
  loadLegs: PropTypes.func,
  routes: PropTypes.array,
  routesInFlight: PropTypes.bool,
  loadRoutes: PropTypes.func
};

FormInput.defaultProps = {
  bookingSummary: [],
  canAdmin: false,
  checkboxFormItemLayout: {},
  dataSource: {},
  form: {},
  inputFormItemLayout: {},
  itemTags: [],
  statuses: [],
  statusesInFlight: false,
  itemTagsInFlight: false,
  loadTypes() {
  },
  loadStatus() {
  },
  onFieldsChange() {
  },
  onValuesChange() {
  },
  readonly: false,
  themeName: '',
  loadTravelModes() {
  },
  travelModes: [],
  travelModesInFlight: false,
  legs: [],
  legsInFlight: false,
  loadLegs() {
  },
  routes: [],
  routesInFlight: false,
  loadRoutes() {
  }
};

const ConnectedFormInput = connect(
  mapStateToProps,
  mapDispatchToProps
)(FormInput);

const BookingFormInput = FormWrapper(Form.create({
  mapPropsToFields,
  onFieldsChange,
  onValuesChange
})(ConnectedFormInput));

export default BookingFormInput;
